import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  ShieldAlert,
  AlertTriangle,
  PawPrint,
  KeyRound,
  Lock,
  Key,
  PhoneCall,
  Ban,
  UserRound,
  Eye,
  Loader2,
} from "lucide-react";
import { revealAccessSafetyFact } from "../../api/accessSafety";
import "./AccessSafetyCard.css";

/* ⚠ Access & Safety card — appointment detail. Renders the `access_safety` block
   (Terminal B's frozen contract, docs/ACCESS_SAFETY_FACTS.md): active facts,
   hazards first, gate_code/key_location masked with an audit-logged tap-to-reveal.
   The array is ALWAYS present ([] → explicit "no notes"), so the card is honest
   about what's on file. Copy is frontend-owned; field names are the server's. */

const FACT_META = {
  hazard: { label: "Hazard", Icon: AlertTriangle, danger: true },
  do_not_service_area: { label: "Do not service", Icon: Ban, danger: true },
  animal: { label: "Animal on property", Icon: PawPrint },
  locked_gate: { label: "Locked gate", Icon: Lock },
  gate_code: { label: "Gate code", Icon: KeyRound },
  key_location: { label: "Key location", Icon: Key },
  call_before_arrival: { label: "Call before arrival", Icon: PhoneCall },
  resident_flag: { label: "Resident note", Icon: UserRound },
};

const fmtDate = (iso) => {
  if (!iso) return "";
  // iso is YYYY-MM-DD; anchor at noon so the local date never rolls back a day.
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

// "Last confirmed Jun 10, 2026 — technician note"; drop the date clause when null.
const provenance = (fact) => {
  const when = fmtDate(fact.last_confirmed_at);
  const src = fact.source || "note";
  return when ? `Last confirmed ${when} — ${src}` : `Source: ${src}`;
};

// Compact relative age for a stale fact's re-verify cue ("18mo", "2y").
const relativeAge = (iso) => {
  if (!iso) return null;
  const then = new Date(`${iso}T12:00:00`).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days < 30) return `${Math.max(days, 1)}d`;
  const months = Math.floor(days / 30);
  return months < 24 ? `${months}mo` : `${Math.floor(months / 12)}y`;
};

function FactRow({ fact }) {
  const meta = FACT_META[fact.fact_type] || { label: fact.fact_type, Icon: ShieldAlert };
  const { Icon } = meta;
  const [revealing, setRevealing] = useState(false);
  const [revealed, setRevealed] = useState(null); // the unmasked value once fetched

  const reveal = async () => {
    if (revealing || revealed != null) return;
    setRevealing(true);
    try {
      const { data } = await revealAccessSafetyFact(fact.fact_id);
      setRevealed(String(data?.value ?? ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reveal — try again.");
    } finally {
      setRevealing(false);
    }
  };

  const showReveal = fact.masked && revealed == null;
  const valueText = revealed != null ? revealed : fact.display_value;

  const staleAge = fact.stale ? relativeAge(fact.last_confirmed_at) : null;

  return (
    <div className={`assc-row${meta.danger ? " assc-row--danger" : ""}${fact.stale ? " assc-row--stale" : ""}`}>
      <span className="assc-row-icon" aria-hidden="true"><Icon size={16} /></span>
      <div className="assc-row-body">
        <div className="assc-row-top">
          <span className="assc-row-type">{meta.label}</span>
          <span className={`assc-row-value${fact.masked ? " assc-mono" : ""}`}>{valueText}</span>
          {fact.stale ? (
            <span className="assc-stale" title="Past its re-verify window — confirm it's still accurate">
              unverified{staleAge ? ` · ${staleAge} ago` : ""} · verify
            </span>
          ) : null}
          {showReveal ? (
            <button
              type="button"
              className="assc-reveal"
              onClick={reveal}
              disabled={revealing}
              aria-label={`Reveal ${meta.label}`}
            >
              {revealing ? <Loader2 size={12} className="assc-spin" /> : <Eye size={12} />}
              {revealing ? "Revealing…" : "Reveal"}
            </button>
          ) : null}
          {fact.masked && revealed != null ? (
            <span className="assc-logged" title="This reveal is recorded in the access log">
              revealed · logged
            </span>
          ) : null}
        </div>
        <div className="assc-row-prov">{provenance(fact)}</div>
      </div>
    </div>
  );
}

export default function AccessSafetyCard({ facts }) {
  if (!Array.isArray(facts)) return null;
  const hasFacts = facts.length > 0;
  return (
    <section className={`apd-card assc-card${hasFacts ? "" : " assc-card--empty"}`} aria-label="Access and safety">
      <div className="assc-head">
        <ShieldAlert size={16} className="assc-head-icon" aria-hidden="true" />
        <span className="assc-head-title">Access &amp; Safety</span>
      </div>
      {hasFacts ? (
        <div className="assc-rows">
          {facts.map((fact) => (
            <FactRow key={fact.fact_id} fact={fact} />
          ))}
        </div>
      ) : (
        <div className="assc-empty">No access notes on file for this customer.</div>
      )}
    </section>
  );
}
